import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAppContext } from "@context/state";

const TITLE_ID = "sai-explainer-title";

const Section = ({ title, children }) => (
  <section className="space-y-1">
    <h3 className="m-0 text-base font-semibold text-gray-900">{title}</h3>
    <div className="text-base text-gray-500 leading-relaxed">{children}</div>
  </section>
);

// Right-side drawer explaining what an SAI is. MUI Drawer traps focus,
// closes on Esc and returns focus to the trigger on close.
const SaiExplainerDrawer = ({ open, onClose }) => {
  const { routeToExpertMeeting } = useAppContext();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": TITLE_ID,
        sx: {
          width: { xs: "100%", sm: 582 },
          borderTopLeftRadius: { sm: 24 },
          borderBottomLeftRadius: { sm: 24 },
        },
      }}
    >
      <div className="px-6 py-3 border-b border-gray-200">
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ border: "1px solid #EAECF0" }}
        >
          <CloseIcon fontSize="small" aria-hidden="true" />
        </IconButton>
      </div>

      <div className="px-6 py-6 space-y-10">
        <h2 id={TITLE_ID} className="m-0 text-2xl font-bold text-gray-900">
          What is an SAI, and how is it calculated?
        </h2>

        <Section title="Is my SAI what I’ll pay?">
          Not exactly. A college subtracts your SAI from its yearly cost to find
          your need. Some colleges cover all of that need, many cover only part,
          and merit scholarships can bring the price down further.
        </Section>

        <Section title="Why are there two?">
          The FAFSA and the CSS Profile use different formulas and count some
          income and assets differently, so the numbers rarely match. Each
          college uses one of them, or both.
        </Section>

        <Section title="How it’s calculated?">
          {/* Read as a sentence by screen readers; the symbols are visual only */}
          <p className="sr-only">
            College’s yearly cost minus your SAI equals the need-based aid you
            may get.
          </p>
          <div
            aria-hidden="true"
            className="flex flex-wrap items-center gap-2 my-2 text-sm"
          >
            <span className="px-3 py-1 rounded-full border border-gray-300 text-gray-700">
              College’s yearly cost
            </span>
            <span className="text-gray-500">−</span>
            <span className="px-3 py-1 rounded-full border border-primary-200 bg-primary-100 text-primary-600">
              Your SAI
            </span>
            <span className="text-gray-500">=</span>
            <span className="px-3 py-1 rounded-full border border-success-200 bg-[#ECFDF3] text-success">
              Need-based aid you may get
            </span>
          </div>
          Every college starts with this math. Schools that cover full need give
          you all of that aid; many others give only part. Merit scholarships
          are separate and can lower the price further.
        </Section>

        <Section title="Can it change?">
          Yes. It is recalculated every year, and some decisions, like where
          savings are held, can affect it.{" "}
          <button
            type="button"
            onClick={routeToExpertMeeting}
            className="text-primary-500 underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-primary-500 rounded"
          >
            Ask an expert what is worth looking at
          </button>
        </Section>
      </div>
    </Drawer>
  );
};

export default SaiExplainerDrawer;
